import { PostgrestError } from "@supabase/supabase-js";
import { useQuery, UseQueryOptions, QueryKey } from "@tanstack/react-query";

/**
 * Generic hook for Supabase queries using React Query
 * 
 * Standardises data fetching with automatic caching, loading states, and error handling.
 * 
 * @example
 * ```tsx
 * const { data: memories, isLoading, error, refetch } = useSupabaseQuery(
 *   ['memories', projectId],
 *   async () => {
 *     const { data, error } = await supabase
 *       .from('memories')
 *       .select('*')
 *       .eq('project_id', projectId);
 *     
 *     if (error) throw error;
 *     return data;
 *   },
 *   { enabled: !!projectId }
 * );
 * ```
 */
export function useSupabaseQuery<TData = any>(
  queryKey: QueryKey,
  queryFn: () => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, PostgrestError>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TData, PostgrestError>({
    queryKey,
    queryFn,
    retry: 1,
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

/**
 * Hook for Supabase queries that depend on user authentication
 * 
 * Automatically disables the query if no user is present.
 */
export function useAuthenticatedSupabaseQuery<TData = any>(
  queryKey: QueryKey,
  queryFn: () => Promise<TData>,
  userId: string | undefined,
  options?: Omit<UseQueryOptions<TData, PostgrestError>, 'queryKey' | 'queryFn' | 'enabled'>
) {
  return useSupabaseQuery<TData>(
    queryKey,
    queryFn,
    {
      enabled: !!userId,
      ...options,
    }
  );
}
